import { ArrowRight, FileText, CheckSquare, Zap, BookOpen } from 'lucide-react';
import { Button } from '@foundation/components/ui/button';

type IntroductionStepProps = {
  onNext: () => void;
  onSkip?: () => void;
};

const WIZARD_STEPS = [
  {
    number: '01',
    title: 'Select a Standard Document',
    description:
      'Browse our library of industry-recognised standard documents. Choose the framework that best aligns with your organisation\'s compliance obligations.',
  },
  {
    number: '02',
    title: 'Confirm Document Details',
    description:
      'Review your selected standard document alongside your organisation profile. Revisr uses this context to tailor the generated policy to your specific requirements.',
  },
  {
    number: '03',
    title: 'Generate Your Policy',
    description:
      'Revisr\'s AI engine drafts a structured, audit-ready policy document derived from the chosen standard. Review, refine, and save it to your policy library.',
  },
];

const HOW_IT_WORKS = [
  {
    icon: BookOpen,
    label: 'Standard-Aligned',
    description: 'Every policy is grounded in a recognised standard document from your industry.',
  },
  {
    icon: FileText,
    label: 'Audit-Ready Output',
    description: 'Policies are structured for compliance audits, regulatory reviews, and internal governance.',
  },
  {
    icon: CheckSquare,
    label: 'Organisation-Aware',
    description: 'Your organisation profile shapes the policy scope, jurisdiction, and applicable clauses.',
  },
  {
    icon: Zap,
    label: 'Instant First Draft',
    description: 'Skip months of manual drafting. Revisr produces a complete first draft in seconds.',
  },
];

export const IntroductionStep = ({ onNext, onSkip }: IntroductionStepProps) => {
  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Hero section */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-semibold tracking-wide mb-5">
          <Zap className="h-3.5 w-3.5 flex-shrink-0" />
          Sample Policy Wizard
        </div>
        <h1 className="text-3xl font-bold text-foreground tracking-tight mb-3">
          Create your first policy document
        </h1>
        <p className="text-muted-foreground text-base max-w-xl mx-auto leading-relaxed">
          Revisr transforms recognised standard documents into structured, organisation-specific
          policies — ready for audit, review, and distribution.
        </p>
      </div>

      {/* How it works */}
      <div className="grid grid-cols-2 gap-3 mb-8 sm:grid-cols-4">
        {HOW_IT_WORKS.map(({ icon: Icon, label, description }) => (
          <div
            key={label}
            className="flex flex-col gap-2 p-4 rounded-xl border border-border bg-card hover:border-primary/30 hover:bg-primary/[0.02] transition-all duration-200"
          >
            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon className="h-4 w-4 text-primary" />
            </div>
            <p className="text-sm font-semibold text-foreground">{label}</p>
            <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
          </div>
        ))}
      </div>

      {/* Step-by-step breakdown */}
      <div className="rounded-2xl border border-border bg-card p-6 mb-8">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-5">
          What happens next
        </p>
        <div className="flex flex-col gap-0">
          {WIZARD_STEPS.map((s, i) => (
            <div key={s.number} className="flex gap-4">
              {/* Connector */}
              <div className="flex flex-col items-center">
                <div className="h-8 w-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[10px] font-bold text-primary">{s.number}</span>
                </div>
                {i < WIZARD_STEPS.length - 1 && (
                  <div className="w-px flex-1 bg-border my-1.5" />
                )}
              </div>

              {/* Content */}
              <div className={`pb-5 ${i === WIZARD_STEPS.length - 1 ? 'pb-0' : ''}`}>
                <p className="text-sm font-semibold text-foreground mb-0.5">{s.title}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{s.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="flex flex-col items-center gap-3">
        <div className="flex items-center gap-3 flex-wrap justify-center">
          <Button
            size="lg"
            onClick={onNext}
            className="gap-2 px-8 font-semibold text-sm shadow-sm"
          >
            Boost Your First Policy
            <ArrowRight className="h-4 w-4" />
          </Button>
          {onSkip && (
            <>
              <span className="text-xs text-muted-foreground">or</span>
              <Button
                size="lg"
                variant="outline"
                onClick={onSkip}
                className="px-8 font-semibold text-sm"
              >
                Skip and go to dashboard
              </Button>
            </>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Takes less than 2 minutes &mdash; your policy library is one step away.
        </p>
      </div>
    </div>
  );
};
